import { Component, OnInit, input, output, inject, signal, effect } from '@angular/core';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { firstValueFrom } from 'rxjs';
import { GoogleMapMarkersService } from './google-map-markers.service';
import { MAP_CONFIG } from '@core/utils';
import { IGeocodeResultV2, IMarkerData } from '@core/interface';
import { GoogleMapsApiService, GoogleMapsScriptLoaderService } from '@core/services';

/**
 * Componente do Google Maps para exibir marcadores e permitir interação
 * Permite visualizar uma localização inicial e marcadores adicionais
 */
@Component({
  selector: 'lib-google-dynamic-map',
  templateUrl: 'google-dynamic-map.component.html',
  standalone: true,
  imports: [GoogleMap, MapMarker],
})
export class GoogleDynamicMapComponent implements OnInit {
  // Serviços injetados
  readonly #googleMapApisService = inject(GoogleMapsApiService);
  readonly #googleMapsScriptLoaderService = inject(GoogleMapsScriptLoaderService);
  readonly #mapMarkersService = inject(GoogleMapMarkersService);

  // Configurações iniciais do mapa vindas da configuração
  mapConfig = MAP_CONFIG;
  zoom: number = MAP_CONFIG.DEFAULT_ZOOM;
  center: { lat: number; lng: number } = MAP_CONFIG.DEFAULT_CENTER;

  // === INPUTS (dados recebidos do componente pai) ===
  height = input<string>('400px'); // Altura do mapa
  icon = input.required<string>(); // URL do ícone customizado para os marcadores
  markers = input<IMarkerData[]>([]); // Lista de marcadores para exibir (pode ser vazia, 1 ou vários)
  isStatic = input<boolean>(false); // Torna o mapa estático (sem interações)
  isAddressSearchClickEnabled = input<boolean>(true); // Habilita busca de endereço ao clicar no mapa (se houver 0 ou 1 marcador)

  // === OUTPUTS (eventos enviados para o componente pai) ===
  markerClicked = output<IMarkerData>(); // Evento quando clica em um marcador
  addressFound = output<IGeocodeResultV2>(); // Evento quando encontra endereço (já com o "address" estruturado)

  // === VARIÁVEIS INTERNAS ===
  markersToRender: IMarkerData[] = []; // Lista final de marcadores para renderizar
  selectedMarker: IMarkerData | null = null; // Marcador atualmente selecionado
  address = signal<string>(''); // Endereço atual (model para two-way binding)
  isMapApiLoaded = signal(false);

  // Ícone customizado para os marcadores vindo da configuração
  customIcon = this.getCustomIcon('');

  /**
   * Retorna as opções do mapa baseadas na flag estática
   * Se estático: desabilita todas as interações
   */
  get dynamicMapOptions(): google.maps.MapOptions {
    if (this.isStatic()) {
      return MAP_CONFIG.MAP_OPTIONS.allDisabled;
    }
    return MAP_CONFIG.MAP_OPTIONS.simple;
  }

  /**
   * Cria ícone customizado com verificação se Google Maps API está carregada
   */
  private getCustomIcon(iconUrl: string) {
    if (typeof google !== 'undefined' && google.maps) {
      return {
        url: iconUrl,
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(40, 40)
      };
    }
    // Fallback se Google Maps não estiver disponível
    return null;
  }

  constructor() {
    effect(() => {
      if (this.markers()) {
        // Reage a mudanças na lista de marcadores para atualizar o mapa
        this.initializeMarkers();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.initializeMarkers();

    try {
      await firstValueFrom(this.#googleMapsScriptLoaderService.load());
      this.customIcon = this.getCustomIcon(this.icon());
      this.isMapApiLoaded.set(true);
    } catch (error) {
      console.error('Erro ao carregar o script do Google Maps:', error);
    }
  }

  /**
   * Inicializa os marcadores no mapa usando o serviço
   * Trabalha apenas com a lista de marcadores (pode ser vazia, 1 ou várias)
   */
  private initializeMarkers() {
    const inputMarkers = this.markers();

    if (inputMarkers && inputMarkers.length > 0) {
      // Renderiza todos os marcadores da lista
      this.markersToRender = [...inputMarkers];

      // Calcula centro e zoom baseado nos marcadores fornecidos
      this.center = this.#mapMarkersService.calculateCenter(this.markersToRender);
      this.zoom = this.#mapMarkersService.calculateZoom(this.markersToRender);
    } else {
      // Lista vazia - mapa vazio
      this.markersToRender = [];
      // Mantém centro e zoom padrão da configuração
      this.center = MAP_CONFIG.DEFAULT_CENTER;
      this.zoom = MAP_CONFIG.DEFAULT_ZOOM;
    }
  }

  /**
   * Evento disparado quando clica em um ponto do mapa
   * Busca endereço apenas se houver 0 ou 1 marcador na lista E o mapa não estiver estático
   * Mantém apenas 1 pin no mapa (remove o anterior)
   */
  onMapClick(event: google.maps.MapMouseEvent) {
    if (!event.latLng) return;

    // Se o mapa está estático, não permite interação
    if (this.isStatic()) return;

    const position = event.latLng.toJSON();

    // Só executa busca de endereço se houver 0 ou 1 marcador
    if (this.isAddressSearchClickEnabled()) {
      // Remove todos os marcadores anteriores e adiciona apenas o novo pin
      this.markersToRender = [];

      // Cria novo marcador na posição clicada
      const newMarker: IMarkerData = {
        id: MAP_CONFIG.MARKER_IDS.CLICKED,
        position: position,
        title: ``,
        address: '' // Será preenchido pelo geocode
      };

      // Adiciona o novo marcador
      this.markersToRender = [newMarker];
      this.selectedMarker = newMarker;

      // Centraliza o mapa na nova posição
      this.center = position;

      // Busca o endereço da coordenada
      this.addressFromCoords(position);
    } else {
      console.log('Busca de endereço desabilitada - múltiplos marcadores na lista');
    }
  }

  /**
   * Evento disparado quando clica em um marcador
   * Seleciona o marcador, centraliza o mapa e emite evento para o componente pai
   */
  onMarkerClick(marker: IMarkerData) {
    console.log('Marcador clicado:', marker);

    // Centraliza o mapa no marcador clicado
    this.center = marker.position;

    // Ajusta o zoom para focar no marcador (se necessário)
    if (this.zoom < 15) {
      this.zoom = 15; // Zoom mais próximo para destacar o marcador
    }

    // Emite o evento para o componente pai
    this.markerClicked.emit(marker);

    // Define como marcador selecionado
    this.selectedMarker = marker;

    // Se o marcador já tem endereço, usa ele
    if (marker.address) {
      this.address.set(marker.address);
    }
  }

  /**
   * Busca o endereço de uma coordenada usando Google Maps API
   * Usa o serviço para atualizar o marcador com o endereço encontrado
   */
  private addressFromCoords(position: { lat: number; lng: number }) {
    const search = `${position.lat},${position.lng}`;

    this.#googleMapApisService.geocodeByV2('latlng', search).subscribe({
      next: (data) => {
        const foundAddress = data.results[0].formatted_address;
        this.address.set(foundAddress);
        this.addressFound.emit(data.results[0]);
      },
      error: (error) => {
        console.error('Erro ao buscar endereço:', error);
      }
    });
  }
}
